/**
 * Single spec for public camp URL path segments (aligned with `syncOrgRootPublicRoute` / `campPublicRoutes`).
 *
 * `slugPath` is the canonical path suffix for a location: `""`, `/${orgSlug}`, or `/${orgSlug}/${locationSlug}`
 * using the same normalization as `campPublicRoutes` doc ids (lowercase [a-z0-9-] segments; underscores become hyphens
 * so they never collide with the `_` separator in composite route doc ids).
 *
 * Rules:
 * 1. If the organization has no normalized org slug → `""`.
 * 2. If this location has a normalized location slug → `/${orgSlug}/${locationSlug}` (composite public URL).
 * 3. Else if this location is the **effective canonical** location for the org short URL → `/${orgSlug}` only
 *    (org-root resolves to this location when it has no location slug / no second path segment).
 * 4. Otherwise → `""`.
 *
 * **Effective canonical** location id (same as org-root maintenance):
 * - `campOrganizations.canonicalLocationId` if it matches a location under the org; else
 * - if exactly one location exists under the org, that location’s id; else
 * - `null` (no org-root primary; locations without their own slug have no public path).
 */

/** Keep in sync with `src/utils/campSlugUtils.ts` → `slugifyToCampUrlSegment`. */
function mergeAdjacentSingleLetterSlugParts(s: string): string {
  const parts = s.split("-").filter((p) => p.length > 0);
  if (parts.length === 0) return "";
  const out: string[] = [];
  let buf = "";
  const flushBuf = () => {
    if (buf) {
      out.push(buf);
      buf = "";
    }
  };
  for (const p of parts) {
    if (/^[a-z]$/.test(p)) {
      buf += p;
    } else {
      flushBuf();
      out.push(p);
    }
  }
  flushBuf();
  return out.join("-");
}

export function normalizeSlugSegment(raw: unknown): string {
  if (typeof raw !== "string") return "";
  let s = raw.trim().toLowerCase();
  if (!s) return "";

  s = s.normalize("NFKD").replace(/\p{M}/gu, "");
  s = s.replace(/ß/g, "ss");

  s = s.replace(/@/g, " at ");
  s = s.replace(/&/g, " and ");

  s = s.replace(/\bnorth carolina\b/g, " nc ");

  s = s.replace(/\b(saint|st)\.?\s+/gi, "st ");

  s = s.replace(/['\u2018\u2019]/g, "");

  s = s.replace(/[^a-z0-9]+/g, "-");
  s = s.replace(/-+/g, "-").replace(/^-|-$/g, "");

  return mergeAdjacentSingleLetterSlugParts(s);
}

/** Same rule as `syncOrgRootPublicRoute` for picking the primary `locationId`. */
export function resolveEffectiveCanonicalLocationId(
  canonicalLocationIdField: unknown,
  orgLocationIds: string[]
): string | null {
  if (typeof canonicalLocationIdField === "string" && canonicalLocationIdField.trim()) {
    const c = canonicalLocationIdField.trim();
    if (orgLocationIds.some((id) => id === c)) return c;
  }
  if (orgLocationIds.length === 1) return orgLocationIds[0] ?? null;
  return null;
}

export function computeCampSlugPathForLocation(params: {
  orgSlugNormalized: string;
  locationId: string;
  locationSlugNormalized: string;
  effectiveCanonicalLocationId: string | null;
}): string {
  const O = params.orgSlugNormalized;
  if (!O) return "";
  const Ls = params.locationSlugNormalized;
  if (Ls) return `/${O}/${Ls}`;
  if (params.effectiveCanonicalLocationId === params.locationId) return `/${O}`;
  return "";
}

/** Treat missing / non-string Firestore values as "". */
export function coerceSlugPathFromDoc(raw: unknown): string {
  return typeof raw === "string" ? raw : "";
}
